import { Entity } from '@backstage/catalog-model';

export interface RoleSlot {
  newKey: string;
  oldKey: string;
  label: string;
}

export const ROLES: RoleSlot[] = [
  { newKey: 'team/lead', oldKey: 'team/lead-email', label: 'Engineering Manager' },
  { newKey: 'team/pm', oldKey: 'team/pm-email', label: 'Product Manager' },
  { newKey: 'team/tech-lead', oldKey: 'team/tech-lead-email', label: 'Tech Lead' },
  { newKey: 'team/architect', oldKey: 'team/architect-email', label: 'Architect' },
];

// PAT-771: every level except the single root 'company' entity can carry
// leadership annotations — matches the same allowlist TeamActions uses for
// Edit/Archive, gated on the catalog-normalized team/level annotation rather
// than spec.type (which can still carry legacy pre-rename strings).
export const LEADERSHIP_LEVELS = ['function', 'sub-function', 'xp', 'team'];

/** Whether a Group entity's level supports leadership slots at all — mirrors
 * the allowlist `EntityTeamLeadershipCard` uses to decide whether it renders. */
export function supportsLeadership(entity: Entity): boolean {
  const level = entity.metadata.annotations?.['team/level'] ?? 'team';
  return LEADERSHIP_LEVELS.includes(level);
}

/** A leadership slot counts as filled by either its new ref-format
 * annotation or its legacy `*-email` annotation. */
export function isSlotFilled(annotations: Record<string, string | undefined>, slot: RoleSlot): boolean {
  return Boolean(annotations[slot.newKey] || annotations[slot.oldKey]);
}

/** Counts filled leadership slots, but only for levels where leadership is
 * supported at all — an entity outside that allowlist (e.g. the root
 * 'company' entity) never has slots to count, even if it carries stale
 * leadership annotations, since no Leadership card renders for it either. */
export function countFilledLeadershipSlots(entity: Entity): number {
  if (!supportsLeadership(entity)) return 0;
  const annotations = entity.metadata.annotations ?? {};
  return ROLES.filter(role => isSlotFilled(annotations, role)).length;
}
